import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { API_URL, UserRoles, formatDate, getCertificateStatus } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  Building2, 
  Award, 
  AlertTriangle,
  TrendingUp,
  Clock,
  ArrowRight,
  XCircle,
  GraduationCap
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const StatCard = ({ icon: Icon, title, value, description, color = 'slate', link }) => (
  <Card className="card-metric group">
    <div className="flex items-start justify-between">
      <div className={`p-3 rounded-xl bg-${color}-100`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
      {link && (
        <Link to={link} className="opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowRight className="w-5 h-5 text-slate-400 hover:text-[#2C9DE1]" />
        </Link>
      )}
    </div>
    <div className="mt-4">
      <span className="stat-number">{value}</span>
      <p className="text-sm font-medium text-slate-600 mt-1">{title}</p>
      {description && (
        <p className="text-xs text-slate-400 mt-1">{description}</p>
      )}
    </div>
  </Card>
);

const RecentCertificates = ({ certificates }) => (
  <Card className="col-span-full lg:col-span-2">
    <CardHeader>
      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Recent Certificates
      </CardTitle>
      <CardDescription>Latest certificates issued</CardDescription>
    </CardHeader>
    <CardContent>
      {certificates.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No certificates issued yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {certificates.slice(0, 5).map((cert) => {
            const status = getCertificateStatus(cert.expiry_date);
            return (
              <div 
                key={cert.id} 
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                    <Award className="w-5 h-5 text-[#2C9DE1]" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{cert.person_name}</p>
                    <p className="text-sm text-slate-500">{cert.course_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono text-slate-600">{cert.certificate_number}</p>
                  <Badge 
                    variant={status === 'active' ? 'default' : status === 'expiring' ? 'secondary' : 'destructive'}
                    className={`
                      ${status === 'active' ? 'bg-emerald-100 text-emerald-800' : ''}
                      ${status === 'expiring' ? 'bg-amber-100 text-amber-800' : ''}
                      ${status === 'expired' ? 'bg-red-100 text-red-800' : ''}
                    `}
                  >
                    {status === 'active' ? 'Active' : status === 'expiring' ? 'Expiring Soon' : 'Expired'}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {certificates.length > 0 && (
        <Link to="/certificates">
          <Button variant="ghost" className="w-full mt-4 text-[#2C9DE1] hover:text-[#1A7BB8]">
            View All Certificates
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      )}
    </CardContent>
  </Card>
);

const ExpiringCertificates = ({ certificates }) => {
  // Already filtered & sorted server-side (expiring within 30 days, soonest first)
  const expiringSoon = certificates;
  
  return (
    <Card className="col-span-full lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Expiring Soon
        </CardTitle>
        <CardDescription>Certificates expiring in the next 30 days</CardDescription>
      </CardHeader>
      <CardContent>
        {expiringSoon.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No certificates expiring soon</p>
          </div>
        ) : (
          <div className="space-y-3">
            {expiringSoon.slice(0, 5).map((cert) => (
              <div 
                key={cert.id} 
                className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100"
              >
                <div>
                  <p className="font-medium text-slate-900">{cert.person_name}</p>
                  <p className="text-sm text-slate-500">{cert.course_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-amber-600 font-medium">Expires</p>
                  <p className="text-sm text-slate-600">{formatDate(cert.expiry_date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Expired Classes Component
const ExpiredClasses = ({ classes, courses, navigate, total }) => {
  // Already filtered server-side (status=expired) and sorted by completion date desc
  const expiredClasses = classes;

  return (
    <Card className="col-span-full lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <XCircle className="w-5 h-5 text-red-500" />
          Expired Classes
        </CardTitle>
        <CardDescription>Classes with expired certificates</CardDescription>
      </CardHeader>
      <CardContent>
        {expiredClasses.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No expired classes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {expiredClasses.slice(0, 5).map((cls) => {
              const course = courses.find(c => c.id === cls.course_id);
              const completionDate = cls.date_completed || cls.date_start;
              const expiryDate = new Date(completionDate);
              expiryDate.setFullYear(expiryDate.getFullYear() + (course?.expiration_years || 3));
              
              return (
                <div 
                  key={cls.id} 
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100 cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => navigate(`/classes/${cls.id}/edit`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-red-200">
                      <GraduationCap className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{cls.course_name}</p>
                      <p className="text-sm text-slate-500">
                        {cls.student_ids?.length || 0} students • {formatDate(completionDate)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-red-100 text-red-800">Expired</Badge>
                    <p className="text-xs text-slate-500 mt-1">{formatDate(expiryDate.toISOString())}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {total > 5 && (
          <Link to="/classes?status=expired">
            <Button variant="ghost" className="w-full mt-4 text-red-600 hover:text-red-700">
              View All ({total} expired)
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

// Expiring Soon Classes Component
const ExpiringClasses = ({ classes, courses, navigate, total }) => {
  const now = new Date();
  
  // Already filtered server-side (status=expiring, within 90 days);
  // sort by computed expiry date (soonest first)
  const expiringClasses = [...classes].sort((a, b) => {
    // Sort by expiry date (soonest first)
    const courseA = courses.find(c => c.id === a.course_id);
    const courseB = courses.find(c => c.id === b.course_id);
    const expiryA = new Date(a.date_completed || a.date_start);
    const expiryB = new Date(b.date_completed || b.date_start);
    expiryA.setFullYear(expiryA.getFullYear() + (courseA?.expiration_years || 3));
    expiryB.setFullYear(expiryB.getFullYear() + (courseB?.expiration_years || 3));
    return expiryA - expiryB;
  });

  return (
    <Card className="col-span-full lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Classes Expiring Soon
        </CardTitle>
        <CardDescription>Classes with certificates expiring in 90 days</CardDescription>
      </CardHeader>
      <CardContent>
        {expiringClasses.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No classes expiring soon</p>
          </div>
        ) : (
          <div className="space-y-3">
            {expiringClasses.slice(0, 5).map((cls) => {
              const course = courses.find(c => c.id === cls.course_id);
              const completionDate = cls.date_completed || cls.date_start;
              const expiryDate = new Date(completionDate);
              expiryDate.setFullYear(expiryDate.getFullYear() + (course?.expiration_years || 3));
              const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
              
              return (
                <div 
                  key={cls.id} 
                  className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => navigate(`/classes/${cls.id}/edit`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-amber-200">
                      <GraduationCap className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{cls.course_name}</p>
                      <p className="text-sm text-slate-500">
                        {cls.student_ids?.length || 0} students • {formatDate(completionDate)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-amber-100 text-amber-800">
                      {daysUntilExpiry} days left
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">{formatDate(expiryDate.toISOString())}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {total > 5 && (
          <Link to="/classes?status=expiring">
            <Button variant="ghost" className="w-full mt-4 text-amber-600 hover:text-amber-700">
              View All ({total} expiring)
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'all_time', label: 'All Time' },
];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageTitle('Dashboard');
  const [stats, setStats] = useState({});
  const [certificates, setCertificates] = useState([]);
  const [expiringCerts, setExpiringCerts] = useState([]);
  const [expiredClasses, setExpiredClasses] = useState({ items: [], total: 0 });
  const [expiringClasses, setExpiringClasses] = useState({ items: [], total: 0 });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [period, setPeriod] = useState('this_year');

  const fetchStats = useCallback(async (selectedPeriod) => {
    setStatsLoading(true);
    try {
      const statsRes = await axios.get(`${API_URL}/dashboard/stats`, {
        params: { period: selectedPeriod },
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Expiry data comes pre-filtered from the server — the collections are
        // far larger than one page, so client-side filtering of a capped
        // fetch would miss everything beyond the newest records.
        const [statsRes, certsRes, expCertsRes, expiredClsRes, expiringClsRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/dashboard/stats`, { params: { period } }),
          axios.get(`${API_URL}/certificates?page_size=10`),
          axios.get(`${API_URL}/certificates?expiring_soon=true&expiring_days=30&sort_by=expiry_date&sort_order=asc&page_size=10`),
          axios.get(`${API_URL}/classes?status=expired&sort_by=date_completed&sort_order=desc&page_size=50`),
          axios.get(`${API_URL}/classes?status=expiring&page_size=200`),
          axios.get(`${API_URL}/courses`)
        ]);
        setStats(statsRes.data);
        setCertificates(certsRes.data.items || certsRes.data);
        setExpiringCerts(expCertsRes.data.items || expCertsRes.data);
        setExpiredClasses({ items: expiredClsRes.data.items || [], total: expiredClsRes.data.total || 0 });
        setExpiringClasses({ items: expiringClsRes.data.items || [], total: expiringClsRes.data.total || 0 });
        setCourses(coursesRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchStats(period);
    }
  }, [period, loading, fetchStats]);

  const isAdmin = user?.role === UserRoles.ADMIN || user?.role === UserRoles.TRAINING_ADMIN;
  const isInstructor = user?.role === UserRoles.INSTRUCTOR;
  const isCompany = user?.role === UserRoles.COMPANY;
  const isStudent = user?.role === UserRoles.STUDENT;

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-slate-500 mt-1">
            Here's what's happening with your training portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Period:</span>
          <Select value={period} onValueChange={setPeriod} disabled={statsLoading}>
            <SelectTrigger className="w-[180px]" data-testid="dashboard-period-select">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`dashboard-period-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Admin/Training Admin Stats */}
      {isAdmin && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Users}
              title="Total Students"
              value={stats.total_students || 0}
              color="blue"
              link="/students"
            />
            <StatCard
              icon={BookOpen}
              title="Courses"
              value={stats.total_courses || 0}
              color="emerald"
              link="/courses"
            />
            <StatCard
              icon={Calendar}
              title="Classes"
              value={stats.total_classes || 0}
              color="violet"
              link="/classes"
            />
            <StatCard
              icon={Building2}
              title="Companies"
              value={stats.total_companies || 0}
              color="orange"
              link="/companies"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Award}
              title="Total Certificates"
              value={stats.total_certificates || 0}
              color="rose"
              link="/certificates"
            />
            <StatCard
              icon={AlertTriangle}
              title="Expiring Soon"
              value={stats.expiring_soon || 0}
              description="Within 30 days"
              color="amber"
              link="/expiry-reminders"
            />
          </div>

          {/* Quick Action: Expiry Reminders */}
          {(stats.expiring_soon > 0 || stats.total_certificates > 0) && (
            <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Certificate Expiry Reminders
                      </h3>
                      <p className="text-sm text-slate-500">
                        Send reminder emails to companies with expiring certificates
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/expiry-reminders')}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    View & Send Reminders
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expired and Expiring Classes */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <ExpiredClasses classes={expiredClasses.items} total={expiredClasses.total} courses={courses} navigate={navigate} />
            <ExpiringClasses classes={expiringClasses.items} total={expiringClasses.total} courses={courses} navigate={navigate} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <RecentCertificates certificates={certificates} />
            <ExpiringCertificates certificates={expiringCerts} />
          </div>
        </>
      )}

      {/* Instructor Stats */}
      {isInstructor && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard
              icon={Calendar}
              title="My Classes"
              value={stats.my_classes || 0}
              color="violet"
              link="/classes"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RecentCertificates certificates={certificates} />
          </div>
        </>
      )}

      {/* Company Stats */}
      {isCompany && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard
              icon={Users}
              title="Total Employees"
              value={stats.total_employees || 0}
              color="blue"
            />
            <StatCard
              icon={Award}
              title="Certificates"
              value={stats.total_certificates || 0}
              color="emerald"
              link="/certificates"
            />
            <StatCard
              icon={AlertTriangle}
              title="Expiring Soon"
              value={stats.expiring_soon || 0}
              description="Within 30 days"
              color="amber"
              link="/certificates"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RecentCertificates certificates={certificates} />
            <ExpiringCertificates certificates={expiringCerts} />
          </div>
        </>
      )}

      {/* Student Stats */}
      {isStudent && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <StatCard
              icon={Award}
              title="My Certificates"
              value={stats.my_certificates || 0}
              color="emerald"
              link="/certificates"
            />
          </div>

          <RecentCertificates certificates={certificates} />
        </>
      )}
    </div>
  );
};

export default Dashboard;
